import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Search, TicketCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { adminListTickets } from '../../utils/adminApi';

const STATUS_OPTIONS = [
  { value: '',               label: 'All Statuses' },
  { value: 'open',           label: 'Open' },
  { value: 'acknowledged',   label: 'Acknowledged' },
  { value: 'in_progress',    label: 'In Progress' },
  { value: 'waiting_on_user',label: 'Waiting on User' },
  { value: 'resolved',       label: 'Resolved' },
  { value: 'closed',         label: 'Closed' },
  { value: 'wont_fix',       label: "Won't Fix" },
];

const PRIORITY_OPTIONS = [
  { value: '',         label: 'All Priorities' },
  { value: 'low',      label: 'Low' },
  { value: 'medium',   label: 'Medium' },
  { value: 'high',     label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const STATUS_COLORS = {
  open:             '#3B82F6',
  acknowledged:     '#8B5CF6',
  in_progress:      '#F59E0B',
  waiting_on_user:  '#EF4444',
  resolved:         '#10B981',
  closed:           '#6B7280',
  wont_fix:         '#DC2626',
};

const STATUS_LABELS = {
  open:             'Open',
  acknowledged:     'Acknowledged',
  in_progress:      'In Progress',
  waiting_on_user:  'Waiting on User',
  resolved:         'Resolved',
  closed:           'Closed',
  wont_fix:         "Won't Fix",
};

const PRIORITY_COLORS = {
  low:      'var(--clr-text-muted)',
  medium:   'var(--clr-warning)',
  high:     'var(--clr-error)',
  critical: '#FF0000',
};

function fmt(iso) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const PAGE_SIZE = 25;

export default function AdminSupportPage() {
  const { adminKey } = useAdminAuth();
  const navigate     = useNavigate();

  const [data,       setData]       = useState({ tickets: [], total: 0 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [page,       setPage]       = useState(1);
  const [search,     setSearch]     = useState('');
  const [filterTenant,   setFilterTenant]   = useState('');
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterPriority, setFilterPriority] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminListTickets(adminKey, {
        tenant:   filterTenant   || undefined,
        status:   filterStatus   || undefined,
        priority: filterPriority || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [adminKey, filterTenant, filterStatus, filterPriority, page]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [filterTenant, filterStatus, filterPriority, search]);

  const tickets    = data.tickets || [];
  const totalPages = data.pages || Math.max(1, Math.ceil((data.total || tickets.length) / PAGE_SIZE));

  // Client-side search filter on ref / title / user
  const visible = search.trim()
    ? tickets.filter((t) => {
        const q = search.toLowerCase();
        return (
          t.ticket_ref.toLowerCase().includes(q) ||
          t.title.toLowerCase().includes(q) ||
          (t.user_email || '').toLowerCase().includes(q) ||
          (t.user_name  || '').toLowerCase().includes(q)
        );
      })
    : tickets;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">
            <TicketCheck size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Support Tickets
          </h1>
          <p className="admin-muted" style={{ fontSize: 13, marginTop: 4 }}>
            All tenant support tickets across InqLect
          </p>
        </div>
        <button className="admin-btn-icon" onClick={load} title="Refresh" disabled={loading}>
          <RefreshCw size={15} className={loading ? 'spin' : ''} />
        </button>
      </div>

      {/* Filters */}
      <div className="admin-filters" style={{ gap: 8, flexWrap: 'wrap' }}>
        <div className="admin-search-wrap">
          <Search size={14} className="admin-search-icon" />
          <input
            className="admin-input admin-search"
            placeholder="Search ref, title, user…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <input
          className="admin-input"
          style={{ width: 160 }}
          placeholder="Tenant ID"
          value={filterTenant}
          onChange={(e) => setFilterTenant(e.target.value.trim())}
        />

        <select
          className="admin-input"
          style={{ width: 160 }}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select
          className="admin-input"
          style={{ width: 140 }}
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
        >
          {PRIORITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="admin-error" style={{ marginBottom: 12 }}>{error}</div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Ref</th>
              <th>Tenant</th>
              <th>Title</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Submitted By</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="admin-table-empty">Loading…</td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={7} className="admin-table-empty">No tickets found.</td>
              </tr>
            ) : (
              visible.map((t) => (
                <tr
                  key={t.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/inqlect-admin/support/${t.ticket_ref}`)}
                >
                  <td>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--clr-accent-light)' }}>
                      {t.ticket_ref}
                    </span>
                  </td>
                  <td>
                    <span className="admin-tag">{t.tenant_id}</span>
                  </td>
                  <td style={{ maxWidth: 260 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{t.title}</span>
                    {t.comment_count > 0 && (
                      <span className="as-comment-count">{t.comment_count}</span>
                    )}
                  </td>
                  <td>
                    <span style={{ color: PRIORITY_COLORS[t.priority], fontSize: 12, fontWeight: 700, textTransform: 'capitalize' }}>
                      {t.priority}
                    </span>
                  </td>
                  <td>
                    <span
                      className="admin-badge"
                      style={{
                        background: `color-mix(in srgb, ${STATUS_COLORS[t.status] || '#94A3B8'} 15%, transparent)`,
                        color: STATUS_COLORS[t.status] || '#94A3B8',
                        border: `1px solid color-mix(in srgb, ${STATUS_COLORS[t.status] || '#94A3B8'} 35%, transparent)`,
                      }}
                    >
                      {STATUS_LABELS[t.status] || t.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    <div>{t.user_name || '—'}</div>
                    <div className="admin-muted">{t.user_email}</div>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--clr-text-muted)', whiteSpace: 'nowrap' }}>
                    {fmt(t.updated_at)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="as-pagination">
          <button
            className="admin-btn-icon"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft size={15} />
          </button>
          <span style={{ fontSize: 13, color: 'var(--clr-text-secondary)' }}>
            Page {page} of {totalPages}
          </span>
          <button
            className="admin-btn-icon"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
